import { persist } from "zustand/middleware";
import { createWithEqualityFn } from "zustand/traditional";

import getUser from "../utils/cognito/getUser";
import newPasswordChallengeCognito from "../utils/cognito/newPasswordChallenge";
import requestPasswordCognito from "../utils/cognito/requestPassword";
import resetPasswordCognito from "../utils/cognito/resetPassword";
import signInCognito from "../utils/cognito/signin";
import signOutCognito from "../utils/cognito/signout";

const useStore = createWithEqualityFn(
  persist(
    (set, get) => ({
      data: {
        user: null,
      },
      actions: {
        // =======================
        // API ACTION CALLS
        // =======================
        handleSignIn: async (data) => {
          try {
            // Call Sign In
            const signInResponse = await signInCognito(
              data.loginEmail,
              data.loginPassword
            );

            if (signInResponse.status === "NEW_PASSWORD_REQUIRED") {
              get().actions.handleSetChallengeUser({
                challengeUserAttributes:
                  signInResponse?.challengeUserAttributes,
                cognitoUser: signInResponse?.cognitoUser,
              });
              return {
                responseCode: 302,
                route: "/new-user",
              };
            }

            // Once Signed In Call Get User Attributes
            const userDataArray = await getUser(signInResponse?.username);
            const userData = userDataArray.reduce(
              (obj, item) => Object.assign(obj, { [item.Name]: item.Value }),
              {}
            );

            const filteredUserData = {
              address: userData?.address,
              org: userData?.["custom:organization"],
              role: userData?.["custom:role"],
              sponsor: userData?.["custom:sponsor"],
              email: userData?.email,
              email_verified: userData?.email_verified,
              firstName: userData?.given_name,
              lastName: userData?.family_name,
              fullName: userData?.name,
              username: signInResponse?.username,
              accessToken: signInResponse?.accessToken,
            };

            // Store User Data
            get().actions.handleSetUser(filteredUserData);

            return { responseCode: 200 };
          } catch (error) {
            return { responseCode: 400, message: error };
          }
        },
        handleSignOut: async (username) => {
          try {
            await signOutCognito(username);

            get().actions.handleSetUser(null);

            return { responseCode: 200 };
          } catch (error) {
            return { responseCode: 400, message: error };
          }
        },
        handleRequestPassword: async (data) => {
          try {
            await requestPasswordCognito(data.loginEmail);
            return { responseCode: 200 };
          } catch (error) {
            return { responseCode: 400, message: error };
          }
        },
        handleResetPassword: async (data) => {
          try {
            await resetPasswordCognito(
              data.loginEmail,
              data.token,
              data.loginPassword
            );
            return { responseCode: 200 };
          } catch (error) {
            return { responseCode: 400, message: error };
          }
        },
        handleNewPasswordChallenge: async (data) => {
          try {
            await newPasswordChallengeCognito(
              data.loginPassword,
              data.challengeUser,
              data.cognitoUser
            );
            get().actions.handleSetChallengeUser(null);
            return { responseCode: 200 };
          } catch (error) {
            return { responseCode: 400, message: error };
          }
        },
        // =======================
        // STATE ACTIONS SETTERS
        // =======================
        handleSetUser: (data) => {
          set((s) => ({
            data: {
              ...s.data,
              user: data,
            },
          }));
        },
        handleSetChallengeUser: (data) => {
          set((s) => ({
            data: {
              ...s.data,
              challengeUser: data,
            },
          }));
        },
      },
    }),
    {
      name: "remi-user",
      partialize: (state) => ({ data: state.data }),
    }
  )
);

export default useStore;
